import type { BudgetDisplayLocationResult } from "utils/resolveBudgetDisplayLocation";
import { isTaxSavingsCurrencySupported } from "../budgetTaxIncentiveNetting";

export interface TaxSavingsPromptCopy {
  message: string;
  /** Null when changing the production base would not help. */
  linkLabel: string | null;
}

export type TaxSavingsPromptInput = Pick<
  BudgetDisplayLocationResult,
  "productionBaseLabel" | "taxState" | "usesStripboardLocations"
> & { budgetCurrency: string | null | undefined };

/** Explains why no tax savings estimate is shown, given the budget's production base. */
export function getTaxSavingsPromptCopy(input: TaxSavingsPromptInput): TaxSavingsPromptCopy {
  if (!isTaxSavingsCurrencySupported(input.budgetCurrency)) {
    return {
      message: "Tax savings estimates are available for budgets in USD.",
      linkLabel: null,
    };
  }

  if (input.taxState) {
    return {
      message: `${input.taxState} has no active film incentive program in our catalog, so there are no tax savings to estimate.`,
      linkLabel: "Change production base",
    };
  }

  if (input.usesStripboardLocations) {
    return {
      message:
        "Tax savings estimates use your production base state. Scene locations from the stripboard don't set one.",
      linkLabel: "Set production base",
    };
  }

  if (input.productionBaseLabel) {
    return {
      message: `Tax savings estimates cover U.S. state programs. ${input.productionBaseLabel} isn't matched to a U.S. state.`,
      linkLabel: "Change production base",
    };
  }

  return {
    message: "Set a production base to see estimated tax savings for this budget.",
    linkLabel: "Set production base",
  };
}
